import type { RoutePathOption, RoutePlan, RouteSegmentPlan } from '@gpx-spa/geo-domain'

function segmentKey(fromStopIndex: number, toStopIndex: number): string {
  return `${fromStopIndex}:${toStopIndex}`
}

function cloneSegment(segment: RouteSegmentPlan, sequence: number): RouteSegmentPlan {
  return {
    sequence,
    fromStopIndex: segment.fromStopIndex,
    toStopIndex: segment.toStopIndex,
    paths: [...segment.paths],
  }
}

export function mergeRoutePlansBySegment(
  google: RoutePlan | null | undefined,
  learned: RoutePlan | null | undefined,
): RoutePlan {
  const byKey = new Map<string, RouteSegmentPlan>()

  for (const segment of google?.segments ?? []) {
    byKey.set(
      segmentKey(segment.fromStopIndex, segment.toStopIndex),
      cloneSegment(segment, segment.sequence),
    )
  }

  for (const segment of learned?.segments ?? []) {
    const key = segmentKey(segment.fromStopIndex, segment.toStopIndex)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, cloneSegment(segment, segment.sequence))
      continue
    }
    existing.paths = [...existing.paths, ...segment.paths]
  }

  const segments = [...byKey.values()].sort((a, b) => {
    if (a.fromStopIndex !== b.fromStopIndex) {
      return a.fromStopIndex - b.fromStopIndex
    }
    return a.toStopIndex - b.toStopIndex
  })
  segments.forEach((segment, index) => {
    segment.sequence = index
  })

  return {
    segments,
    meta: {
      provider:
        google?.segments?.length && learned?.segments?.length
          ? 'merged'
          : google?.segments?.length
            ? 'google'
            : learned?.segments?.length
              ? 'learned'
              : undefined,
    },
  }
}

export function googleFirstThenLearned(paths: RoutePathOption[]): RoutePathOption[] {
  const google: RoutePathOption[] = []
  const learned: RoutePathOption[] = []
  const other: RoutePathOption[] = []
  for (const path of paths) {
    if (path.meta?.provider === 'google') google.push(path)
    else if (path.meta?.provider === 'learned') learned.push(path)
    else other.push(path)
  }
  return [...google, ...learned, ...other]
}
